/**
 * The point numbers a market type is offered at, as a trader writes them into the board: `5, 7, 9`.
 * Two Table Tennis markets are configured this way — the targets a race is run to and the points the
 * point winner is quoted on — and they are read by one parser, as the backend reads them through one
 * `TtConfiguredNumbers`. Which numbers a deployment actually offers is its own configuration, which
 * this page cannot read, so the board states them per request and an empty box asks for its own set.
 */
export interface ParsedNumbers {
  /**
   * The numbers to price. Undefined for empty text, which asks for the deployment's own set — an empty
   * list is a different request, one that prices none of that market at all.
   */
  numbers?: number[];
  /** Set when the text could not be read; `numbers` is then undefined. */
  error?: string;
}

// One number, written as it is offered: "7".
const NUMBER = /^\d{1,3}$/;

/**
 * Reads a comma-separated list of point numbers. `noun` names what one of them is, so the two callers
 * report an unreadable list in their own market's words; nothing else about them differs.
 */
export function parseConfiguredNumbers(input: string, noun: string): ParsedNumbers {
  const trimmed = input.trim();
  if (!trimmed) {
    return {};
  }

  const numbers: number[] = [];
  for (const token of trimmed.split(',')) {
    const text = token.trim();
    if (!text) {
      continue;
    }
    if (!NUMBER.test(text)) {
      return { error: `"${text}" is not a ${noun} — write each one as a whole number like 7` };
    }
    const value = parseInt(text, 10);
    if (value < 1) {
      return { error: `a ${noun} is at least 1 — the points of a set are counted from the first` };
    }
    if (!numbers.includes(value)) {
      numbers.push(value);
    }
  }
  return { numbers };
}
